import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, LogOut, Search, Trash2, RefreshCw, Phone } from "lucide-react";
import { toast } from "sonner";
import { verifyAdmin, fetchBookings, updateBookingStatus, deleteBooking } from "@/lib/api";
import AdminDayBlocker from "@/components/AdminDayBlocker";

const STATUS_ORDER = ["pending", "confirmed", "completed"];
const STATUS_STYLES = {
  pending: "border-amber-400/50 text-amber-300 bg-amber-400/10",
  confirmed: "border-volt/50 text-volt bg-volt/10",
  completed: "border-emerald-400/50 text-emerald-300 bg-emerald-400/10",
};

const AdminPage = () => {
  const [authed, setAuthed] = useState(!!sessionStorage.getItem("ert_admin_key"));
  const [key, setKey] = useState("");
  const [checking, setChecking] = useState(false);
  const [bookings, setBookings] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    try {
      setBookings(await fetchBookings());
    } catch {
      sessionStorage.removeItem("ert_admin_key");
      setAuthed(false);
      toast.error("Session expired. Please sign in again.");
    }
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setChecking(true);
    try {
      await verifyAdmin(key.trim());
      sessionStorage.setItem("ert_admin_key", key.trim());
      setAuthed(true);
    } catch {
      toast.error("Invalid access key.");
    } finally {
      setChecking(false);
    }
  };

  const cycleStatus = async (b) => {
    const next = STATUS_ORDER[(STATUS_ORDER.indexOf(b.status) + 1) % STATUS_ORDER.length];
    try {
      await updateBookingStatus(b.booking_id, next);
      setBookings((prev) => prev.map((x) => (x.booking_id === b.booking_id ? { ...x, status: next } : x)));
    } catch {
      toast.error("Could not update status.");
    }
  };

  const remove = async (b) => {
    try {
      await deleteBooking(b.booking_id);
      setBookings((prev) => prev.filter((x) => x.booking_id !== b.booking_id));
      toast.success("Booking deleted.");
    } catch {
      toast.error("Could not delete booking.");
    }
  };

  const filtered = (bookings || []).filter((b) =>
    `${b.name} ${b.phone} ${b.service} ${b.email || ""} ${b.address || ""}`.toLowerCase().includes(search.toLowerCase())
  );

  if (!authed) {
    return (
      <div className="min-h-screen bg-carbon flex items-center justify-center px-4 noise-overlay relative">
        <motion.form
          data-testid="admin-login-form"
          onSubmit={handleLogin}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-sm bg-panel border border-edge rounded-2xl p-8"
        >
          <div className="font-mono2 text-[10px] uppercase tracking-[0.35em] text-volt mb-2">Team Access</div>
          <h1 className="font-syne font-extrabold uppercase text-2xl text-slate-100">Bookings Console</h1>
          <input
            data-testid="admin-key-input"
            type="password"
            required
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Access key"
            className="mt-6 w-full bg-carbon border border-edge rounded-lg px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-volt transition-colors"
          />
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={checking}
            className="mt-4 w-full flex items-center justify-center gap-2 bg-volt text-carbon font-syne font-bold text-sm uppercase tracking-widest rounded-full px-6 py-3.5 hover:bg-white hover:text-carbon disabled:opacity-60 transition-colors"
          >
            {checking ? <Loader2 size={15} className="animate-spin" /> : null}
            {checking ? "Verifying…" : "Enter"}
          </button>
          <a href="/" data-testid="admin-back-home" className="block mt-5 text-center font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500 hover:text-volt transition-colors">
            Back to site
          </a>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-carbon text-slate-100 noise-overlay relative">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-mono2 text-[10px] uppercase tracking-[0.35em] text-volt mb-1">Elite Rims &amp; Trims</div>
            <h1 className="font-syne font-extrabold uppercase text-2xl sm:text-3xl">Bookings Console</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={load}
              className="flex items-center gap-2 border border-edge rounded-full px-4 py-2 font-mono2 text-[11px] uppercase tracking-widest text-slate-300 hover:text-volt hover:border-volt/60 transition-colors"
            >
              <RefreshCw size={13} /> Refresh
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={() => { sessionStorage.removeItem("ert_admin_key"); setAuthed(false); }}
              className="flex items-center gap-2 border border-edge rounded-full px-4 py-2 font-mono2 text-[11px] uppercase tracking-widest text-slate-300 hover:text-red-400 hover:border-red-400/60 transition-colors"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>

        <div className="mt-8 relative">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="admin-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone or service…"
            className="w-full bg-panel border border-edge rounded-full pl-11 pr-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-volt transition-colors"
          />
        </div>

        <div data-testid="admin-bookings-list" className="mt-6 space-y-3">
          {bookings === null ? (
            <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-volt" size={26} /></div>
          ) : filtered.length === 0 ? (
            <div data-testid="admin-empty-state" className="py-20 text-center font-mono2 text-xs uppercase tracking-widest text-slate-500">
              No bookings found
            </div>
          ) : (
            filtered.map((b) => (
              <motion.div
                key={b.booking_id}
                data-testid={`admin-booking-row-${b.booking_id.slice(0, 8)}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                className="bg-panel border border-edge rounded-xl p-5 flex flex-wrap items-center gap-x-8 gap-y-3 hover:border-volt/40 transition-colors"
              >
                <div className="min-w-[160px]">
                  <div className="font-syne font-bold text-slate-100">{b.name}</div>
                  <a href={`tel:${b.phone.replace(/\s/g, "")}`} className="flex items-center gap-1.5 font-mono2 text-xs text-volt hover:underline mt-0.5">
                    <Phone size={11} /> {b.phone}
                  </a>
                  {b.email && <div className="font-mono2 text-[11px] text-slate-500 mt-0.5 break-all">{b.email}</div>}
                </div>
                <div className="flex-1 min-w-[180px]">
                  <div className="text-sm text-slate-200">{b.service}</div>
                  <div className="font-mono2 text-[11px] text-slate-500 mt-0.5">
                    {b.date} · {b.time}{b.payment ? ` · ${b.payment === "cash" ? "Cash" : "Bank transfer"}` : ""}
                  </div>
                  {b.address && <div className="font-mono2 text-[11px] text-slate-500 mt-0.5">{b.address}</div>}
                  {b.notes && <div className="text-xs text-slate-400 mt-1.5 italic">{b.notes}</div>}
                </div>
                <div className="flex items-center gap-3 ml-auto">
                  <button
                    data-testid={`admin-status-btn-${b.booking_id.slice(0, 8)}`}
                    onClick={() => cycleStatus(b)}
                    className={`font-mono2 text-[10px] uppercase tracking-[0.2em] border rounded-full px-4 py-1.5 transition-colors ${STATUS_STYLES[b.status]}`}
                  >
                    {b.status}
                  </button>
                  <button
                    data-testid={`admin-delete-btn-${b.booking_id.slice(0, 8)}`}
                    onClick={() => remove(b)}
                    aria-label="Delete booking"
                    className="w-8 h-8 rounded-full border border-edge flex items-center justify-center text-slate-400 hover:text-red-400 hover:border-red-400/60 transition-colors"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </motion.div>
            ))
          )}
        </div>

        <AdminDayBlocker />
      </div>
    </div>
  );
};

export default AdminPage;
